import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

// Compact, column-optimized Sprint Progress for the Insights panel

const GET_CURRENT_USER_FOR_SPRINT = gql`
  query GetCurrentUserForInsightsSprint {
    getCurrentUser { id triggering_events { event_type completed_at } }
  }
`;

const GET_SPRINTS = gql`
  query GetSprintsForInsightsSprint($ownerId: ID, $status: [SprintStatusEnum!]) {
    sprints(owner_user_id: $ownerId, status: $status) { id name status startOn endOn items { id status dueAt } }
  }
`;

const toDate = (d) => (d ? new Date(d) : null);
const daysBetween = (a, b) => Math.max(0, Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24)));

const CompactBurndown = ({ startOn, endOn, totalItems, completedItems }) => {
  const start = toDate(startOn) || new Date();
  const end = toDate(endOn) || new Date(start.getTime() + 14 * 24 * 3600 * 1000);
  const today = new Date();
  const totalDays = Math.max(1, daysBetween(start, end));
  const dayIdxToday = Math.min(totalDays, Math.max(0, daysBetween(start, today)));

  const width = 360; // logical viewport width for crisp SVG
  const height = 100; // compact default; SVG will scale with container height
  const pad = { l: 28, r: 6, t: 8, b: 18 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;

  const x = (d) => pad.l + (innerW * d) / totalDays;
  const y = (v) => pad.t + innerH - (innerH * v) / Math.max(1, totalItems);

  const remaining = Math.max(0, totalItems - completedItems);
  const ideal = `M ${x(0)} ${y(totalItems)} L ${x(totalDays)} ${y(0)}`;
  const actual = `M ${x(0)} ${y(totalItems)} L ${x(dayIdxToday)} ${y(remaining)}`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full min-h-[100px]">
      <line x1={pad.l} y1={pad.t + innerH} x2={pad.l + innerW} y2={pad.t + innerH} stroke="var(--succession-border)" />
      <line x1={pad.l} y1={pad.t} x2={pad.l} y2={pad.t + innerH} stroke="var(--succession-border)" />
      <path d={ideal} stroke="var(--succession-text-muted)" strokeDasharray="4 4" fill="none" />
      <path d={actual} stroke="var(--succession-primary)" strokeWidth="2" fill="none" />
      <circle cx={x(dayIdxToday)} cy={y(remaining)} r="3" fill="var(--succession-primary)" />
      <text x={pad.l + 2} y={pad.t + 10} textAnchor="start" fontSize="4" fill="var(--succession-text-secondary)">Tasks</text>
      <text x={pad.l + innerW} y={pad.t + innerH + 14} textAnchor="end" fontSize="4" fill="var(--succession-text-secondary)">Time</text>
    </svg>
  );
};

const InsightsSprintProgress = () => {
  const { data: cu } = useQuery(GET_CURRENT_USER_FOR_SPRINT, { fetchPolicy: 'cache-first' });
  const ownerId = cu?.getCurrentUser?.id;
  const { data: sData, loading: sLoading, error } = useQuery(GET_SPRINTS, { variables: { ownerId, status: ['ACTIVE'] }, skip: !ownerId, fetchPolicy: 'cache-first' });

  const events = cu?.getCurrentUser?.triggering_events || [];
  const hasAnyCompletedEvent = Array.isArray(events) && events.some((e) => e && e.completed_at);
  const sprints = sData?.sprints || [];
  const active = sprints.find((s) => String(s.status).toUpperCase() === 'ACTIVE');
  const hasCurrentSprint = Boolean(active);

  if (sLoading || !ownerId) return <div className="w-full h-full min-h-[200px] animate-pulse bg-transparent rounded-md" aria-hidden="true" />;
  if (error) return <div className="text-succession-error text-xs"><FormattedMessage id="succession.sprint_progress.error" /></div>;

  if (!hasCurrentSprint) {
    return (
      <div className="flex flex-col h-full">
        <h3 className="text-succession-text-primary m-0 p-0"><FormattedMessage id="succession.sprint_progress.title" /></h3>
        <div className="text-succession-text-secondary text-xs mb-2"><FormattedMessage id="succession.sprint_progress.subtitle" /></div>
        <div className="bg-transparent rounded-lg p-4 text-center flex-1 min-h-[200px] overflow-hidden flex items-center justify-center">
          <div>
            <h3 className="text-succession-text-primary m-0 p-0 mb-1"><FormattedMessage id="succession.sprint_progress.measure_title" /></h3>
            {hasAnyCompletedEvent ? (
              <>
                <div className="text-succession-text-secondary text-sm mb-2"><FormattedMessage id="succession.sprint_progress.has_plan_msg" /></div>
                <a href="/succession/advisor-map" className="inline-flex items-center px-3 py-1.5 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover text-sm"><FormattedMessage id="succession.sprint_progress.create_sprint" /></a>
              </>
            ) : (
              <>
                <div className="text-succession-text-secondary text-sm mb-2"><FormattedMessage id="succession.sprint_progress.no_plan_msg" /></div>
                <button onClick={() => window.location.href = '/succession/plans'} className="inline-flex items-center px-3 py-1.5 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover text-sm"><FormattedMessage id="succession.sprint_progress.start_plan" /></button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const items = active?.items || [];
  const totalItems = items.length;
  const completedItems = items.filter((it) => String(it.status).toLowerCase() === 'completed').length;
  const remaining = Math.max(0, totalItems - completedItems);
  const start = toDate(active?.startOn) || new Date();
  const end = toDate(active?.endOn) || new Date(start.getTime() + 14 * 24 * 3600 * 1000);
  const today = new Date();
  const daysLeft = Math.max(0, daysBetween(today, end));

  return (
    <div className="flex flex-col h-full">
      <h3 className="text-succession-text-primary m-0 p-0"><FormattedMessage id="succession.sprint_progress.title" /></h3>
      <div className="text-succession-text-secondary text-xs mb-2"><FormattedMessage id="succession.sprint_progress.subtitle" /></div>
      <div className="bg-transparent rounded-lg p-3 flex-1 min-h-[200px] overflow-hidden flex flex-col">
        <div className="grid grid-cols-3 gap-3 text-[11px]">
          <div className="text-succession-text-secondary"><FormattedMessage id="succession.sprint_progress.done" /> <span className="text-succession-text-primary font-semibold">{completedItems}</span> / <span className="text-succession-text-primary font-semibold">{totalItems}</span></div>
          <div className="text-succession-text-secondary"><FormattedMessage id="succession.sprint_progress.remaining" /> <span className="text-succession-text-primary font-semibold">{remaining}</span></div>
          <div className="text-succession-text-secondary"><FormattedMessage id="succession.sprint_progress.days_left" /> <span className="text-succession-text-primary font-semibold">{daysLeft}</span></div>
        </div>
        <div className="mt-1 flex-1 overflow-hidden">
          <CompactBurndown startOn={active?.startOn} endOn={active?.endOn} totalItems={totalItems} completedItems={completedItems} />
        </div>
        <div className="mt-1 text-[11px] text-succession-text-secondary">
          <span className="mr-4"><span className="inline-block w-3 h-1 align-middle bg-succession-primary mr-1" /><FormattedMessage id="succession.sprint_progress.actual" /></span>
          <span><span className="inline-block w-3 h-1 align-middle bg-succession-text-muted mr-1" style={{ borderBottom: '1px dashed var(--succession-text-muted)' }} /><FormattedMessage id="succession.sprint_progress.ideal" /></span>
        </div>
        <div className="mt-auto pt-1 flex justify-center">
          <a href="/succession/sprint" className="text-xs text-succession-primary hover:text-succession-primary-hover"><FormattedMessage id="succession.sprint_progress.review_sprint" /></a>
        </div>
      </div>
    </div>
  );
};

export default InsightsSprintProgress;




